import React, { useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { FaPen, FaPlus, FaTrash } from 'react-icons/fa';

import { Container, Cabecalho, ButtonsContainer } from './styles';
import { TabelaContainer, TabelaVazia, Table, Th, Td, Tr, valorCelula, ehNumero } from '../Tabela/styles';

import api from '../../services/api';
import Context, { IContext } from '../../context/Context';
import useWindowDimensions from '../../utils/WindowDimensions';
import Button from '../Button';
import ModalInserirProduto from '../ModalInserirProduto';

export interface ICampo {
  label: string;
  campo: string;
  /** laytel.pre: casas decimais dos números */
  pre?: string;
}

interface GridGenericoProps {
  entidade: string;
  codigo: string | number;
  botoesVisiveis?: boolean;
  dadosGridGenerico: Record<string, any>[];
  setDadosGridGenerico: React.Dispatch<React.SetStateAction<Record<string, any>[]>>
  // Muda depois de gravar o movimento: recarrega os itens (cod dos novos e quantidade gravada)
  recarregar?: number;
  // Itens alterados pelo usuário (inserir, alterar, excluir): a ficha recalcula os totais
  onItensAlterados?: (itens: Record<string, any>[]) => void;
}

function novoItemGrid(produto: any, qua: number, val: number) {
  return {
    codmer: +produto?.cod,
    mer: produto?.mer,
    codbar: produto?.codbar,
    qua,
    quaent: 0,
    val,
    valdes: 0,
    valcus: +produto?.valcus || 0,
    porcom: 0,
    aliicm: 0,
    numlot: '',
    coditepedsai: 0,
    coditeped: 0,
    oricon: 0,
    valvenpro: 0,
    cupcan: 0,
    hor: '',
    bri: 0,
    codpedpro: 0,
    valunigra: 0,
    valcasbac: 0
  };
}

// Colunas do grid quando o pedido ainda não tem itens (a API devolve {} e não há de onde tirar os nomes)
const CAMPOS_ITEM_NOVO = Object.keys(novoItemGrid({}, 0, 0));

export default function GridGenerico({ entidade, codigo, botoesVisiveis, dadosGridGenerico, setDadosGridGenerico, recarregar, onItensAlterados }: GridGenericoProps) {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const { setIsLoadingOverlay }: IContext = useContext(Context);

  const [campos, setCampos] = useState<ICampo[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [linhaSelecionada, setLinhaSelecionada] = useState<any>();
  const [produtoParaEditar, setProdutoParaEditar] = useState<any>();
  // Origem das colunas em `campos`: '<entidade>' (do retorno da API) ou '<entidade>:modelo' (pedido sem itens).
  // O componente é reaproveitado ao trocar de compras para vendas
  const entidadeCampos = useRef<string>();
  // Última requisição feita: respostas atrasadas (de outra entidade ou pedido) são descartadas
  const requisicaoAtual = useRef(0);

  function montarCampos(nomes: string[], origem: string) {
    entidadeCampos.current = origem;
    const campos = nomes.filter(nome => !nome.startsWith('_')).map(campo => ({ label: campo, campo }));
    setCampos(campos);
    getCamposLabel(campos, origem);
  }

  async function getDados(codigo: string | number) {
    try {
      if (!codigo) {
        // Venda nova: ainda não há itens na API, mas o grid precisa das colunas para mostrar os inseridos
        if (entidadeCampos.current?.split(':')[0] !== entidade) montarCampos(CAMPOS_ITEM_NOVO, `${entidade}:modelo`);
        return;
      }

      setIsLoadingOverlay(true);

      const requisicao = ++requisicaoAtual.current;

      const response = await api.get(entidade, {
        params: {
          codped: codigo
        }
      });

      if (requisicao !== requisicaoAtual.current) return;

      if (response.status === 200) {
        // Sem itens a API devolve {} (sem data)
        const itens: Record<string, any>[] = Array.isArray(response.data?.data) ? response.data.data : [];

        if (itens.length >= 1 && entidadeCampos.current !== entidade) {
          // Pegando os nomes dos campos direto do retorno da API
          montarCampos(Object.keys(itens[0]), entidade);
        } else if (itens.length === 0 && entidadeCampos.current?.split(':')[0] !== entidade) {
          montarCampos(CAMPOS_ITEM_NOVO, `${entidade}:modelo`);
        }

        // _quaori/_valori: o que está gravado. Na edição a API soma a qua recebida, então a ficha envia só a diferença
        setDadosGridGenerico(itens.map(item => ({ ...item, _quaori: item.qua, _valori: item.val })));
      }
    } catch (error) {
      console.error(error);
      toast.error(`Erro ao buscar dados. ${entidade}`);
    } finally {
      setTimeout(() => setIsLoadingOverlay(false), 200);
    }
  }

  async function getCamposLabel(camposAtuais: ICampo[], origem: string) {
    try {
      if (!entidade) return;

      let tabela = entidade === 'produto' ? 'mercador' : entidade;

      switch (entidade) {
        case 'produto':
          tabela = 'mercador';
          break;
        case 'cliente':
          tabela = 'cliente';
          break;
        case 'fornecedor':
          tabela = 'forneced';
          break;
        case 'vendas':
          tabela = 'pedsai';
          break;
        case 'compras':
          tabela = 'pedent';
          break;
        default:
          break;
      }

      const response = await api.get(`/layout`, {
        params: {
          tabela
        }
      });

      if (entidadeCampos.current !== origem) return;

      if (response.status === 200) {

        const novosCampos = camposAtuais.map(campo => {
          const campoLista = response.data.DADOS.find((dado: any) => dado?.cam && dado.cam.toLowerCase() === campo.campo.toLowerCase());

          if (campoLista && (campoLista?.vis === '0' || isMobile && campoLista?.vislismob !== '1')) {
            return undefined;
          }

          if (campoLista && campoLista.vis === '1') {
            return {
              ...campo,
              label: campoLista.cap,
              lim: campoLista.lim,
              tipinp: campoLista.tipinp,
              porfic: campoLista.porfic,
              ordlis: campoLista.ordlis,
              pre: campoLista.pre
            }
          }

          return campo;
        }).sort((campoA: any, campoB: any) => {
          if (campoA?.ordlis && campoB?.ordlis) {
            return Number(campoA?.ordlis) - Number(campoB?.ordlis);
          }
          return 0;
        });

        setCampos(novosCampos.filter(Boolean) as ICampo[]);

      }

    } catch (error) {
      console.log(error);
      toast.error(`Erro ao carregar campos. ${entidade}`);
    }
  }

  function alterarItemGridGenerico() {
    if (!linhaSelecionada) return toast.warning('Selecione um item para alterar');
    setProdutoParaEditar(linhaSelecionada);

    setModalVisible(true);
  }

  function alterarItens(itens: Record<string, any>[]) {
    setDadosGridGenerico(itens);
    onItensAlterados?.(itens);
  }

  function excluirItemGridGenerico() {
    if (!linhaSelecionada) return toast.warning('Selecione um item para excluir');

    // Item gravado vai com exc para a API; item ainda não gravado só sai da lista
    alterarItens(dadosGridGenerico
      .map(item => item === linhaSelecionada ? (item?.cod ? { ...item, exc: 'true' } : undefined) : item)
      .filter(Boolean) as Record<string, any>[]);
    setLinhaSelecionada(undefined);
  }

  useEffect(() => {
    if (entidadeCampos.current?.split(':')[0] !== entidade) {
      setCampos([]);
      setProdutoParaEditar(undefined);
    }
    // Os itens são recarregados: a seleção apontaria para um objeto que saiu da lista
    setLinhaSelecionada(undefined);
    getDados(codigo);
  }, [codigo, entidade, recarregar]);

  const itensVisiveis = (dadosGridGenerico || []).filter(item => item?.exc !== 'true');
  const colunaNumerica = (campo: string) => itensVisiveis.length > 0 && ehNumero(itensVisiveis[0][campo]);

  return (
    <Container>
      <Cabecalho>
        <h2>Produtos</h2>
        {botoesVisiveis && <ButtonsContainer>
          <Button
            texto='Inserir'
            tamanho='sm'
            icone={<FaPlus />}
            onClick={() => {
              if (produtoParaEditar) {
                setProdutoParaEditar(undefined);
              }
              setModalVisible(true);
            }}
          />
          <Button
            texto='Alterar'
            tamanho='sm'
            variante='secundario'
            icone={<FaPen />}
            disabled={!linhaSelecionada}
            onClick={() => alterarItemGridGenerico()}
          />
          <Button
            texto='Excluir'
            tamanho='sm'
            variante='perigo'
            icone={<FaTrash />}
            disabled={!linhaSelecionada}
            onClick={() => excluirItemGridGenerico()}
          />
        </ButtonsContainer>}
      </Cabecalho>
      <TabelaContainer>
        <Table>
          <thead>
            <tr>
              {campos.map((campo, index) => (
                <Th key={index} $numero={colunaNumerica(campo.campo)}>{campo.label}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {itensVisiveis.map((item, IndexLinha) => (
              <Tr
                key={IndexLinha}
                $clicavel
                $selecionado={item === linhaSelecionada}
                onClick={() => setLinhaSelecionada(item)}
              >
                {campos.map((campo, indexColuna) => (
                  <Td key={indexColuna} $numero={ehNumero(item[campo.campo])}>
                    {valorCelula(item[campo.campo], campo.pre, campo.campo)}
                  </Td>
                ))}
              </Tr>
            ))}
          </tbody>
        </Table>
        {itensVisiveis.length === 0 && <TabelaVazia>Nenhum produto</TabelaVazia>}
      </TabelaContainer>
      {modalVisible && (
        <ModalInserirProduto
          onClose={() => setModalVisible(false)}
          produtoParaEditar={produtoParaEditar}
          onAdicionar={(produto: any, quaven: number, val: number) => {
            // Alterar: produto é a linha do grid (codmer). Inserir: é o mercador da busca (cod)
            const codmer = +(produtoParaEditar ? produto.codmer : produto.cod);

            const itemExistente = dadosGridGenerico.find(item => +item.codmer === codmer);

            if (itemExistente) {
              alterarItens(dadosGridGenerico.map(item => {
                if (+item.codmer === codmer) {
                  // Inserir de novo um produto excluído o traz de volta
                  return { ...item, qua: quaven, val: val, exc: undefined };
                }

                return item;
              }));

              setModalVisible(false);
              toast.success(`${produto.mer} foi alterado`);
              return;
            }

            alterarItens([...dadosGridGenerico, novoItemGrid(produto, quaven, val)]);
            setModalVisible(false);
            toast.success(`${produto.mer} foi adicionado`);
          }}
        />
      )}
    </Container>
  );
};
